import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminUserCoachRow,
  type AdminUserDetail,
  type AdminUserPause,
  type AdminUserPaybackResult,
  type AdminUserPaybackReview,
  type AdminUserPayment,
  type AdminUserPlan,
  type AdminUserRefundPreview,
  type AdminUserRefundResult,
  type AdminUserRefundType,
  type AdminUserResetResult,
  type AdminUserSelfCheckPoint,
  type AdminUserStartOption,
  type AdminUserStartResult,
  type AdminUserSubscriptionResult,
} from "api/adminApi";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  HelpTip,
  InfoRow,
  Input,
  Modal,
  SectionHeader,
  Select,
  Table,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  Textarea,
  Toggle,
} from "pages/admin/ui";
import {
  actionBlockReason,
  canTerminateState,
  defaultStartUntilValue,
  daysBetweenInputValues,
  expirationAdjustOf,
  shiftDateInputValue,
  EXPIRATION_ADJUST_LABEL,
  refundAmountError,
  refundBasePayment,
  refundedPayments,
  optimisticTerminateResult,
  formatAmount,
  paidAmountLabel,
  refundedAmountLabel,
  formatDate,
  formatDateTime,
  isPauseScheduleMissed,
  meditationMinutesLabel,
  monthDayLabel,
  oauthIssuerLabel,
  pausePlanOf,
  pauseScheduledByLabel,
  pauseScheduleLabel,
  paybackCompletionCondition,
  paybackFirstPaymentCondition,
  paybackGapLabel,
  paybackMeditationSummary,
  paybackPausesLabel,
  paybackExtensionLabel,
  paybackRefundBreakdown,
  paybackRefundItemLabel,
  paybackRefundResult,
  paybackRefundTotal,
  paybackThoughtResult,
  paybackThoughtScoreLabel,
  paybackThoughtSummary,
  paybackWeekActivityLabel,
  paybackWeekDaysLabel,
  paybackUnmetConditions,
  paybackUsageChange,
  paybackWeeklyCondition,
  selfCheckChartPoints,
  selfCheckHoverRanges,
  selfCheckMaxScore,
  selfCheckSeverityLevel,
  selfCheckSummaryLabel,
  selfCheckTicks,
  selfCheckTooltip,
  type PaybackCondition,
  type SelfCheckKind,
  pauseStateLabel,
  paymentStatusLabel,
  planLabel,
  progressStatusLabel,
  restoredExpirationDate,
  subscriptionStatusLabel,
  subscriptionTone,
  todayInputValue,
  utmSummary,
  vendorTypeLabel,
} from "./userFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

// 진행 일차 ⓘ: 서버 ProgramPeriodUtils.programProgressDays 와 같은 규칙
const PROGRAM_DAYS_HELP =
  "프로그램 시작일을 1일차로, 프로그램이 실제로 진행된 날만 셉니다. " +
  "일시정지 기간과 만료·종료 뒤 다시 시작하기까지의 기간은 빠지고, 다시 시작하면 멈춘 다음 일차부터 이어서 셉니다.";

// 열려 있는 액션 팝업. null 이면 닫힌 상태.
type ActionKind =
  | "start"
  | "reset"
  | "refundDowngrade"
  | "refundAmount"
  | "payback"
  | "extend"
  | "pause"
  | "pauseCancel"
  | "resume"
  | "terminate"
  | "nickname"
  | "coach";

/**
 * 회원관리: 회원번호로 찾아 상세를 보고, 기한 조정·일시정지·재개·프로그램 종료·닉네임·코치 변경과
 * 환불(다운그레이드·결제 금액만·프로그램 종료에 딸린 환불)을 처리한다.
 *
 * 버튼은 액션 바 하나에 모은다. 환불만 아래로 떼어 두던 때는 "종료" 와 "환불 + 종료" 가 화면 위아래로
 * 갈라져 둘 중 하나만 눌리는 사고가 났다. 지금은 환불이 프로그램 종료 팝업의 체크 하나로 붙는다.
 */
export default function UserSection() {
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [coaches, setCoaches] = useState<AdminUserCoachRow[]>([]);

  const load = async (userId: number) => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await adminApi.userDetail(userId);
      if (res.code === 0 && res.result) {
        setDetail(res.result);
      } else {
        setDetail(null);
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setDetail(null);
      const status = (e as AxiosError).response?.status;
      setMessage(
        status === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      setLoading(false);
    }
  };

  const search = () => {
    const userId = Number(query.trim());
    if (!query.trim() || Number.isNaN(userId) || userId <= 0) {
      setMessage("회원번호를 숫자로 입력해 주세요");
      return;
    }
    load(userId);
  };

  // 입력이 바뀌면 이전 조회 결과를 즉시 비운다. 낡은 상세를 보고 다른 회원에게 액션이 실행되는 것을 막는다.
  const onQueryChange = (value: string) => {
    setQuery(value);
    setDetail(null);
    setMessage(null);
  };

  // 코치 목록은 코치를 고르는 팝업(프로그램 시작·코치 변경)을 처음 열 때 한 번만 받아 둔다.
  const openWithCoaches = useCallback(
    async (kind: ActionKind) => {
      setAction(kind);
      if (coaches.length > 0) return;
      try {
        const res = await adminApi.userCoachList();
        if (res.code === 0 && res.result) setCoaches(res.result);
      } catch (e) {
        setMessage(FALLBACK_ERROR_MESSAGE);
      }
    },
    [coaches.length]
  );

  // 구독 관련 처리는 결과를 그대로 돌려받아 로컬 패치한다 (재조회 왕복 없이 화면이 즉시 맞는다).
  const patchSubscription = (result: AdminUserSubscriptionResult) => {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            user: { ...prev.user, progressStatus: result.progressStatus },
            subscription: {
              ...prev.subscription,
              status: result.subscriptionStatus,
              subscriptionExpirationDate: result.subscriptionExpirationDate,
              bonusDays: result.bonusDays,
            },
            pause: {
              ...prev.pause,
              isPaused: !!result.pausedAt,
              pausedAt: result.pausedAt,
              resumedAt: result.resumedAt,
              pausedDays: result.pausedAt ? 0 : null,
            },
          }
        : prev
    );
  };

  // 처리 이력은 서버가 다시 계산해 주는 게 정확하다. 액션 후 상세만 조용히 다시 받는다.
  const reload = () => {
    if (detail) load(detail.user.userId);
  };

  // 환불이 붙은 종료는 서버가 상태를 바꾸지 않고 웹훅이 몇 초 뒤에 바꾼다. 바로 다시 받으면 웹훅 전
  // 상태로 화면이 되돌아가므로, 낙관적으로 그린 뒤 잠시 후 한 번만 받되 아직 웹훅 전이면 버린다.
  const patchRefundedPayments = (refund: AdminUserRefundResult) => {
    setDetail((prev) =>
      prev
        ? { ...prev, payments: refundedPayments(prev.payments, refund) }
        : prev
    );
  };
  const reloadAfterWebhook = (userId: number, orderId: string) => {
    window.setTimeout(async () => {
      try {
        const res = await adminApi.userDetail(userId);
        const payment = res.result?.payments.find((p) => p.orderId === orderId);
        if (res.code !== 0 || !res.result || payment?.status === "confirmed")
          return;
        setDetail((prev) =>
          prev?.user.userId === userId ? res.result! : prev
        );
      } catch (e) {
        // 낙관적 화면을 유지한다. 다시 조회하면 실제 값이다
      }
    }, 5000);
  };

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader title="회원 관리">
        <Input
          className="w-56"
          fullWidth={false}
          inputMode="numeric"
          placeholder="회원번호 (userId)"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") search();
          }}
        />
        <Button
          variant="primary"
          onClick={search}
          loading={loading}
          loadingLabel="조회 중"
        >
          조회
        </Button>
      </SectionHeader>

      {message && <ErrorBanner>{message}</ErrorBanner>}

      {!detail && !loading && !message && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 py-20">
          <p className="text-sm font-medium text-slate-600">
            회원번호로 회원을 찾아 주세요
          </p>
          <p className="text-sm text-slate-400">
            상세를 확인한 뒤 기한 조정·일시정지·종료·닉네임·코치 변경을 처리할
            수 있어요.
          </p>
        </div>
      )}

      {loading && !detail && (
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className="p-5">
              <span className="block h-4 w-24 animate-pulse rounded bg-slate-100" />
              <div className="mt-4 flex flex-col gap-2.5">
                {[0, 1, 2, 3].map((j) => (
                  <span
                    key={j}
                    className="block h-4 w-full animate-pulse rounded bg-slate-100"
                  />
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {detail && (
        <>
          <ActionBar
            detail={detail}
            groups={[
              {
                title: "프로그램",
                actions: [
                  { key: "start", label: "시작" },
                  { key: "extend", label: "기한 조정" },
                  detail.pause.isPaused
                    ? { key: "resume", label: "일시정지 해제" }
                    : { key: "pause", label: "일시정지" },
                  ...(detail.pause.scheduledStartDate
                    ? [{ key: "pauseCancel" as const, label: "정지 예약 취소" }]
                    : []),
                  { key: "terminate", label: "종료", variant: "danger" },
                  { key: "reset", label: "초기화", variant: "danger" },
                ],
              },
              {
                title: "이용 정보",
                actions: [
                  { key: "nickname", label: "닉네임 변경" },
                  { key: "coach", label: "코치 변경" },
                ],
              },
              {
                title: "결제",
                actions: [
                  { key: "refundDowngrade", label: "플랜 다운그레이드" },
                  {
                    key: "refundAmount",
                    label: "금액만 환불",
                    variant: "danger",
                  },
                  // 조회만 하지만 환불로 이어지는 검토라 빨강으로 둔다 (운영 요청)
                  { key: "payback", label: "페이백", variant: "danger" },
                ],
              },
            ]}
            onSelect={(kind) =>
              kind === "coach" || kind === "start"
                ? openWithCoaches(kind)
                : setAction(kind)
            }
          />
          <DetailPanel detail={detail} />
          <PaymentTable payments={detail.payments} />
          <ActionLogTable detail={detail} />
        </>
      )}

      {detail && action === "start" && (
        <StartModal
          detail={detail}
          coaches={coaches}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
      {detail &&
        (action === "refundDowngrade" || action === "refundAmount") && (
          <RefundModal
            detail={detail}
            kind={action}
            onClose={() => setAction(null)}
            onDone={() => {
              setAction(null);
              reload();
            }}
          />
        )}
      {detail && action === "payback" && (
        <PaybackModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            setAction(null);
            // 토스 취소는 끝났고 결제 이력·이용 상태는 웹훅을 기다린다. 종료 환불과 같다
            patchRefundedPayments(paybackRefundResult(result));
            reloadAfterWebhook(detail.user.userId, result.canceled[0].orderId);
          }}
        />
      )}
      {detail && action === "reset" && (
        <ResetModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "extend" && (
        <ExpirationAdjustModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "pause" && (
        <PauseModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            // 미래 날짜 예약은 상태를 바꾸지 않는다. 응답의 pausedAt 은 예전 정지의 흔적일 수 있어
            // (자동 재개는 지우지 않는다) 그대로 패치하면 다시 받기 전까지 "정지 중" 으로 보인다.
            if (result.subscriptionStatus === "subscribing_expired")
              patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "pauseCancel" && (
        <PauseCancelModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "resume" && (
        <ResumeModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result) => {
            patchSubscription(result);
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "terminate" && (
        <TerminateModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={(result, refund) => {
            patchSubscription(result);
            setAction(null);
            if (!refund) {
              reload();
              return;
            }
            patchRefundedPayments(refund);
            reloadAfterWebhook(detail.user.userId, refund.canceled[0].orderId);
          }}
        />
      )}
      {detail && action === "nickname" && (
        <NicknameModal
          detail={detail}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
      {detail && action === "coach" && (
        <CoachModal
          detail={detail}
          coaches={coaches}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            reload();
          }}
        />
      )}
    </section>
  );
}

// ------------------------------------------------------------------ 액션 바

type BarAction = { key: ActionKind; label: string; variant?: "danger" };
type BarGroup = { title: string; actions: BarAction[] };

function ActionBar({
  detail,
  groups,
  onSelect,
}: {
  detail: AdminUserDetail;
  groups: BarGroup[];
  onSelect: (kind: ActionKind) => void;
}) {
  // 한 카드 안에서 성격별로 줄을 나눈다. 버튼을 화면 여기저기로 떼면 "종료" 와 "환불" 처럼
  // 한 건으로 들어온 요청이 둘로 갈라져 하나만 눌린다. 붙여 두되 무엇이 무엇인지는 구분한다.
  // 라벨이 명사를 맡고 버튼은 동작만 남긴다. "프로그램 > 프로그램 시작" 처럼 겹쳐 읽히지 않게.
  // 팝업 제목은 "프로그램 종료" 처럼 전체 이름을 그대로 쓴다 (거기엔 그룹 라벨이 없다).
  // danger 는 되돌릴 수 없는 것에만 쓴다. 다운그레이드는 이용이 끊기지 않아 빠져 있다. 페이백만 예외로 조회인데 빨강이다.
  // 일시정지 자리는 상태에 따라 정지/정지 해제로 바뀐다. 정지 예약 취소는 예약이 있을 때만 나온다. 나머지는 고정.
  // 막힌 사유는 목록으로 늘어놓지 않는다. 버튼이 회색이면 못 누른다는 건 이미 보이고,
  // 사유를 전부 적으면 액션 바보다 안내가 길어진다. 이유는 hover(title)로만 알려준다.
  const withReason = groups.map((group) => ({
    ...group,
    actions: group.actions.map((action) => ({
      ...action,
      reason: actionBlockReason(detail, action.key),
    })),
  }));

  return (
    <Card className="flex flex-col gap-3 p-4">
      {withReason.map((group) => (
        <div key={group.title} className="flex flex-wrap items-center gap-2">
          <span className="w-16 shrink-0 whitespace-nowrap text-xs font-medium text-slate-500">
            {group.title}
          </span>
          {group.actions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant ?? "secondary"}
              onClick={() => onSelect(action.key)}
              disabled={!!action.reason}
              // 비활성 버튼에 마우스를 올리면 이유가 보인다. 이제 여기가 유일한 설명이다.
              title={action.reason ?? undefined}
            >
              {action.label}
            </Button>
          ))}
        </div>
      ))}
    </Card>
  );
}

// ------------------------------------------------------------------ 상세 패널

function DetailPanel({ detail }: { detail: AdminUserDetail }) {
  const { user, subscription, pause, coach, referral, acquisition } = detail;
  // 설문 응답은 유입 채널 라벨에 이미 녹아 있어 따로 찍지 않는다. 존재 여부만 빈 상태 판정에 쓴다.
  const survey = acquisition?.survey;
  const webFunnel = acquisition?.webFunnel;
  const utm = webFunnel ? utmSummary(webFunnel) : null;
  const hasAcquisition =
    !!acquisition?.channelLabel || !!survey || !!webFunnel || !!referral;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5">
        <PanelTitle>회원 정보</PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          <InfoRow label="회원번호">{user.userId}</InfoRow>
          <InfoRow label="닉네임">{user.nickname || "없음"}</InfoRow>
          <InfoRow label="이메일">{user.email || "없음"}</InfoRow>
          <InfoRow label="가입 방식">
            {oauthIssuerLabel(user.oauthIssuer)}
          </InfoRow>
          <InfoRow label="가입일">
            {formatDate(user.signedUpDate) || "없음"}
          </InfoRow>
          <InfoRow label="진행 상태">
            {progressStatusLabel(user.progressStatus)}
          </InfoRow>
          <InfoRow label="프로그램 시작">
            {formatDate(user.programStartedAt) || "시작 전"}
          </InfoRow>
          <InfoRow label="진행 일차" help={PROGRAM_DAYS_HELP}>
            {user.programDays > 0
              ? `${user.programDays}일차 (${user.programWeeks}주차)`
              : "시작 전"}
          </InfoRow>
          {user.programStoppedAt && (
            <InfoRow label="중단 일시">
              {formatDateTime(user.programStoppedAt)}
            </InfoRow>
          )}
          {user.leavedAt && (
            <InfoRow label="탈퇴 일시">{formatDateTime(user.leavedAt)}</InfoRow>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <PanelTitle>
          이용 상태
          <Badge tone={subscriptionTone(pause, subscription)}>
            {pause.isPaused
              ? pauseStateLabel(pause)
              : subscriptionStatusLabel(subscription.status)}
          </Badge>
        </PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          <InfoRow label="플랜">{planLabel(user.plan)}</InfoRow>
          <InfoRow label="이용 시작">
            {formatDate(subscription.subscriptionStartedAt) || "없음"}
          </InfoRow>
          <InfoRow label="이용 기한">
            {subscription.subscriptionExpirationDate
              ? `${formatDate(subscription.subscriptionExpirationDate)} (${
                  subscription.expiryDaysLeft ?? 0
                }일 남음)`
              : "없음"}
          </InfoRow>
          <InfoRow label="누적 연장">{`${subscription.bonusDays ?? 0}일`}</InfoRow>
          {pause.scheduledStartDate && <PauseScheduleRows pause={pause} />}
          {pause.isPaused && (
            <InfoRow label="정지 경과">
              {`${pause.pausedDays ?? 0}일 (${formatDate(pause.pausedAt)} 정지)`}
            </InfoRow>
          )}
          <InfoRow label="일시정지 쿼터">
            {`${pause.usedDays} / ${pause.totalDays}일 · ${pause.usedCount} / ${pause.totalCount}회`}
          </InfoRow>
          {pause.reservation && (
            <InfoRow label="회원 예약">
              {`${formatDate(pause.reservation.startDate)} ~ ${formatDate(
                pause.reservation.endDate
              )}`}
            </InfoRow>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <PanelTitle>담당 코치</PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          {coach ? (
            <>
              <InfoRow label="코치">
                {`${coach.coachName} (${coach.coachDisplayName})`}
              </InfoRow>
              <InfoRow label="재직 여부">
                {coach.isActive ? "재직" : "퇴사"}
              </InfoRow>
              <InfoRow label="배정 일시">
                {formatDateTime(coach.coachAssignedAt) || "없음"}
              </InfoRow>
              <InfoRow label="채팅방">
                {coach.hasChatChannel ? "열려 있음" : "없음"}
              </InfoRow>
            </>
          ) : (
            <p className="text-sm text-slate-400">
              아직 배정된 코치가 없어요. 프로그램을 시작하면 배정돼요.
            </p>
          )}
        </div>
      </Card>

      <Card className="p-5">
        <PanelTitle>가입 경로</PanelTitle>
        <div className="mt-3 flex flex-col gap-2">
          {acquisition?.channelLabel && (
            <InfoRow label="유입 채널">{acquisition.channelLabel}</InfoRow>
          )}
          {utm && <InfoRow label="광고 파라미터">{utm}</InfoRow>}
          {webFunnel?.hasAdClickId && (
            <InfoRow label="광고 클릭">클릭 ID 붙어 있음</InfoRow>
          )}
          {webFunnel?.capturedAt && (
            <InfoRow label="첫 방문">
              {formatDateTime(webFunnel.capturedAt)}
            </InfoRow>
          )}
          {referral && (
            <>
              <InfoRow label="레퍼럴 코드">{referral.code}</InfoRow>
              <InfoRow label="최종 유입">
                {formatDateTime(referral.lastTouchedAt) || "없음"}
              </InfoRow>
              <InfoRow label="기여 확정">
                {formatDateTime(referral.attributedAt) || "미확정"}
              </InfoRow>
            </>
          )}
          {!hasAcquisition && (
            <p className="text-sm text-slate-400">
              유입 정보가 남아 있지 않은 회원이에요.
            </p>
          )}
          {user.promotion && (
            <InfoRow label="프로모션">{user.promotion}</InfoRow>
          )}
          {user.benefit && <InfoRow label="혜택">{user.benefit}</InfoRow>}
          {user.organization && (
            <InfoRow label="소속">{user.organization}</InfoRow>
          )}
          {user.isInsider && <InfoRow label="내부자">내부자 계정</InfoRow>}
        </div>
      </Card>
    </div>
  );
}

// 어드민이 미래 날짜로 잡아 둔 정지. 시작일이 지났는데 남아 있으면 그날 실행되지 못한 것이라 따로 표시한다.
function PauseScheduleRows({ pause }: { pause: AdminUserPause }) {
  const missed = isPauseScheduleMissed(pause);
  return (
    <>
      <InfoRow label="정지 예약">
        <span className="flex flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            {pauseScheduleLabel(pause)}
            <Badge tone={missed ? "warn" : "info"}>
              {missed ? "미실행" : "예약"}
            </Badge>
          </span>
          {missed && (
            <span className="text-xs leading-relaxed text-amber-700">
              시작일에 실행되지 않았어요. 예약을 취소하고 다시 잡아 주세요.
            </span>
          )}
        </span>
      </InfoRow>
      <InfoRow label="예약한 사람">{pauseScheduledByLabel(pause)}</InfoRow>
    </>
  );
}

function PanelTitle({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ 결제 이력

function PaymentTable({ payments }: { payments: AdminUserPayment[] }) {
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader title="결제 이력" count={`${payments.length}건`} />
      <p className="text-xs text-slate-500">
        환불은 토스페이먼츠에서 처리가 끝날 때까지 몇 초 걸릴 수 있어요. 표가
        바뀌지 않으면 다시 조회해 주세요.
      </p>
      <Table>
        <THead>
          <TH>주문번호</TH>
          <TH>상태</TH>
          <TH>수단</TH>
          <TH>결제일</TH>
          <TH numeric>결제 금액</TH>
          <TH numeric>환불 금액</TH>
          <TH>취소일</TH>
        </THead>
        <tbody>
          {payments.length === 0 && (
            <TEmpty colSpan={7}>결제 이력이 없는 회원이에요.</TEmpty>
          )}
          {payments.map((payment) => (
            <TRow key={payment.orderId}>
              <TD className="font-mono text-xs">{payment.orderId}</TD>
              <TD>
                <Badge tone={payment.status === "confirmed" ? "ok" : "idle"}>
                  {paymentStatusLabel(payment.status)}
                </Badge>
              </TD>
              <TD muted>{vendorTypeLabel(payment.vendorType)}</TD>
              <TD muted>{formatDate(payment.confirmedAt) || "미승인"}</TD>
              <TD numeric>{paidAmountLabel(payment)}</TD>
              <TD numeric>{refundedAmountLabel(payment)}</TD>
              <TD muted>{formatDate(payment.canceledAt)}</TD>
            </TRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ------------------------------------------------------------------ 처리 이력

function ActionLogTable({ detail }: { detail: AdminUserDetail }) {
  const logs = detail.actionLogs;
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader title="처리 이력" count={`최근 ${logs.length}건`} />
      <Table>
        <THead>
          <TH>일시</TH>
          <TH>담당자</TH>
          <TH>작업</TH>
          <TH>변경 내용</TH>
          <TH>메모</TH>
        </THead>
        <tbody>
          {logs.length === 0 && (
            <TEmpty colSpan={5}>아직 어드민이 처리한 기록이 없어요.</TEmpty>
          )}
          {logs.map((logRow, index) => (
            <TRow key={`${logRow.actionAt}-${index}`}>
              <TD muted>{formatDateTime(logRow.actionAt)}</TD>
              <TD muted>
                {logRow.adminEmail.replace("@orwellhealth.org", "")}
              </TD>
              <TD>{logRow.action}</TD>
              <TD muted className="whitespace-normal">
                {logRow.beforeSummary && logRow.afterSummary
                  ? `${logRow.beforeSummary} → ${logRow.afterSummary}`
                  : logRow.afterSummary || ""}
              </TD>
              <TD muted className="whitespace-normal">
                {logRow.memo || ""}
              </TD>
            </TRow>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ------------------------------------------------------------------ 액션 팝업

// 팝업마다 반복되는 상태(제출 중·오류·메모)와 제출 처리를 한군데로 모은다.
function useAction<T>(onDone: (result: T) => void) {
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (
    call: () => Promise<{ code: number; message?: string; result?: T }>
  ) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await call();
      if (res.code === 0 && res.result !== undefined) {
        onDone(res.result as T);
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  // 입력 화면으로 돌아가 다른 경로를 고르면 앞선 시도의 오류는 더 이상 사실이 아니다.
  return {
    memo,
    setMemo,
    submitting,
    error,
    submit,
    clearError: () => setError(null),
  };
}

function MemoField({
  memo,
  setMemo,
  placeholder = "예: 코치 변경 안내에 따른 보상",
}: {
  memo: string;
  setMemo: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <Field
      label="처리 이력 메모"
      helper="처리 이력에 함께 남아요. 비워 두어도 괜찮아요."
    >
      <Textarea
        rows={2}
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        placeholder={placeholder}
      />
    </Field>
  );
}

function subjectOf(detail: AdminUserDetail) {
  return `${detail.user.userId} · ${detail.user.nickname || "닉네임 없음"}`;
}

type ModalProps = {
  detail: AdminUserDetail;
  onClose: () => void;
  onDone: (result: AdminUserSubscriptionResult) => void;
};

/**
 * 준비 중인 회원을 결제 없이 시작시킨다.
 *
 * 시작하면 담당 코치 배정·채팅방 개설·초기 활동지까지 한 번에 나가고 되돌릴 수 없다.
 * 그래서 무엇이 정해지는지(플랜·이용 기한·코치)를 폼에서 전부 보여준다.
 */
const START_OPTIONS: { value: AdminUserStartOption; label: string }[] = [
  { value: "creator_trial", label: "크리에이터 체험" },
  { value: "event_winner", label: "당첨 이벤트" },
  { value: "simulated_patient", label: "모의내담자 역할" },
];

// 저장되는 값이 화면 라벨과 다르다. 어느 지표에 잡히는지가 운영 판단에 필요해서 그대로 적는다.
const START_OPTION_HELP: Record<AdminUserStartOption, string> = {
  creator_trial:
    '앰버서더 체험 계정과 같은 세팅이에요. 혜택 표기에 "인플루언서", Mixpanel 참여 유형에 "제휴" 가 남아요.',
  event_winner:
    '혜택 표기와 Mixpanel 프로모션에 "인플루언서 이벤트" 가 남아요. 이 회원이 지표에서 인플루언서 유입으로 잡혀요.',
  simulated_patient:
    '코치 교육용 모의 내담자 계정으로 시작해요. 혜택 표기와 Mixpanel 프로모션에 "코치 교육" 이 남아 코치 내담자 목록에 "교육" 라벨이 붙어요. 내부자로 표시돼 담당 인원·지표 집계에서 빠지고, 담당 코치를 비워 두면 관리자 코치에게 배정돼요.',
};

function StartModal({
  detail,
  coaches,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  coaches: AdminUserCoachRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [plan, setPlan] = useState<AdminUserPlan>("challenger");
  const [untilDate, setUntilDate] = useState(defaultStartUntilValue());
  const [coachId, setCoachId] = useState("");
  const [startOption, setStartOption] = useState<AdminUserStartOption | "">("");
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserStartResult>(onDone);

  // 모의내담자 역할은 서버가 마스터로 고정한다. 화면도 같은 값을 보여준다
  const isSimulatedPatient = startOption === "simulated_patient";
  const effectivePlan: AdminUserPlan = isSimulatedPatient ? "master" : plan;

  const run = () =>
    submit(() =>
      adminApi.userStart(detail.user.userId, {
        plan: effectivePlan,
        untilDate,
        ...(coachId && { coachId: Number(coachId) }),
        ...(startOption !== "" && { startOption }),
        memo,
      })
    );

  return (
    <Modal
      title="프로그램 시작"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={!untilDate}
          >
            시작
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="플랜"
          helper={
            isSimulatedPatient
              ? "모의내담자 역할은 마스터로 고정돼요."
              : undefined
          }
        >
          <Select
            value={effectivePlan}
            onChange={(e) => setPlan(e.target.value as AdminUserPlan)}
            disabled={isSimulatedPatient}
          >
            <option value="starter">스타터</option>
            <option value="challenger">챌린저</option>
            <option value="master">마스터</option>
          </Select>
        </Field>

        <Field
          label="이용 종료일"
          helper="기본값은 오늘부터 100일이에요. 결제로 시작할 때와 같아요."
        >
          <Input
            type="date"
            value={untilDate}
            onChange={(e) => setUntilDate(e.target.value)}
          />
        </Field>

        <Field
          label="담당 코치"
          helper="비워 두면 자동 배정 규칙대로 정해져요."
        >
          <Select value={coachId} onChange={(e) => setCoachId(e.target.value)}>
            <option value="">자유 배정 (자동)</option>
            {coaches.map((coach) => (
              <option key={coach.coachId} value={coach.coachId}>
                {`${coach.name} (${coach.assignedPatientCount}/${coach.assignCapCount}명)`}
                {coach.assignedPatientCount >= coach.assignCapCount
                  ? " · 상한 도달"
                  : ""}
                {coach.isMasterPlanExcluded ? " · 마스터 제외" : ""}
                {coach.isAdmin ? " · 관리자" : ""}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="시작 옵션" helper="일반 시작이면 비워 두세요.">
          <Select
            value={startOption}
            onChange={(e) =>
              setStartOption(e.target.value as AdminUserStartOption | "")
            }
          >
            <option value="">선택 안 함</option>
            {START_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        {startOption !== "" && (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {START_OPTION_HELP[startOption]}
          </p>
        )}

        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          시작하면 담당 코치가 배정되고 채팅방이 열리면서 첫 활동지까지 나가요.
          되돌릴 수 없으니 플랜과 이용 기한을 한 번 더 확인해 주세요. 시작일은
          어제로 잡혀서 오늘이 이용 1일차가 돼요.
        </p>

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

type RefundKind = "refundDowngrade" | "refundAmount";

const REFUND_TYPE: Record<RefundKind, AdminUserRefundType> = {
  refundDowngrade: "downgrade",
  refundAmount: "amount_only",
};

const REFUND_TITLE: Record<RefundKind, string> = {
  refundDowngrade: "다운그레이드",
  refundAmount: "결제 금액만 환불",
};

/**
 * 금액 프리셋(전액·수수료 10% 제외)은 서버만 계산할 수 있다. 정책 유형과 활동지 완료 여부를 봐야 한다.
 * 금액을 비운 미리보기가 프리셋 조회다.
 */
function useRefundPresets(
  userId: number,
  type: AdminUserRefundType,
  enabled: boolean
) {
  const [presets, setPresets] = useState<AdminUserRefundPreview | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    adminApi
      .userRefundPreview(userId, { type })
      .then((res) => {
        if (alive && res.code === 0 && res.result) setPresets(res.result);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [userId, type, enabled]);

  return presets;
}

/** 환불 금액 입력 한 벌: 결제 금액만 환불과 프로그램 종료(환불 포함)가 같이 쓴다. */
function RefundAmountSection({
  base,
  amount,
  setAmount,
  presets,
  helper,
}: {
  base: AdminUserPayment | null;
  amount: string;
  setAmount: (value: string) => void;
  presets: AdminUserRefundPreview | null;
  helper?: string;
}) {
  const overBalance = refundAmountError(amount, base?.payAmount);
  return (
    <>
      <InfoRow label="기준 결제">
        <span className="font-mono text-xs">{base?.orderId ?? "없음"}</span>
      </InfoRow>
      {/* 부분 취소가 있었으면 원결제 금액이 아니라 남은 잔액이다. 전액 환불도 이 값이 상한이다. */}
      <InfoRow label="환불 가능 잔액">{formatAmount(base?.payAmount)}</InfoRow>

      <Field label="환불 금액" helper={helper}>
        <Input
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="숫자만 입력"
        />
      </Field>

      {/* 잔액을 넘으면 진행 버튼도 함께 잠긴다. 입력값을 말없이 잘라 버리지는 않는다.
          돈이 걸린 칸에서 타이핑한 숫자가 바뀌면 얼마를 보낸 건지 확신할 수 없다. */}
      {overBalance && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {overBalance}
        </p>
      )}

      {presets && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setAmount(String(presets.fullAmount))}>
            {`전액 ${formatAmount(presets.fullAmount)}`}
          </Button>
          {presets.policyAmount !== presets.fullAmount && (
            <Button onClick={() => setAmount(String(presets.policyAmount))}>
              {`수수료 10% 제외 ${formatAmount(presets.policyAmount)}`}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

/** 실행 직전 확인: 서버가 계산한 값을 그대로 보여준다. 화면에서 본 숫자와 빠지는 숫자가 어긋날 수 없게. */
function RefundPreviewRows({ preview }: { preview: AdminUserRefundPreview }) {
  const row = preview.distribution[0];
  return (
    <>
      <InfoRow label="주문번호">
        <span className="font-mono text-xs">{row.orderId}</span>
      </InfoRow>
      <InfoRow label="환불 금액">{formatAmount(row.cancelAmount)}</InfoRow>
      <InfoRow label="환불 후 잔액">
        {formatAmount(preview.fullAmount - row.cancelAmount)}
      </InfoRow>
      {preview.targetPlanLabel && (
        <InfoRow label="변경 후 플랜">{preview.targetPlanLabel}</InfoRow>
      )}
      <InfoRow label="취소 사유">
        <span className="font-mono text-xs">{row.cancelReason}</span>
      </InfoRow>
    </>
  );
}

/** 이번 취소에 묶이지 않는 플랜 업그레이드 결제. 있으면 토스 콘솔에서 따로 취소해야 한다. */
function LeftoverOrders({ orderIds }: { orderIds: string[] }) {
  if (orderIds.length === 0) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
      <p className="text-sm font-medium text-amber-800">
        같이 취소되지 않는 결제
      </p>
      {orderIds.map((left) => (
        <p key={left} className="mt-1 font-mono text-xs text-amber-700">
          {left}
        </p>
      ))}
      <p className="mt-1 text-xs text-amber-700">
        플랜 업그레이드 결제예요. 필요하면 토스 콘솔에서 따로 취소해 주세요.
      </p>
    </div>
  );
}

/**
 * 다운그레이드와 결제 금액만 환불. 둘 다 흐름이 같다. 입력 → 서버 미리보기 → 확인 → 실행.
 * 프로그램 종료에 딸린 환불은 종료와 한 번에 결정해야 해서 TerminateModal 이 맡는다.
 */
function RefundModal({
  detail,
  kind,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  kind: RefundKind;
  onClose: () => void;
  onDone: () => void;
}) {
  const type = REFUND_TYPE[kind];
  const base = refundBasePayment(detail);
  const hints = base?.planChangeHints ?? [];

  const [amount, setAmount] = useState("");
  const [targetPlan, setTargetPlan] = useState<AdminUserPlan | "">("");
  // 팝업 1회 열림 = 환불 1건. 재시도해도 같은 값을 보내 토스가 중복 취소를 걸러낸다.
  const [requestId] = useState(() => crypto.randomUUID());
  const [preview, setPreview] = useState<AdminUserRefundPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserRefundResult>(onDone);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const presets = useRefundPresets(
    detail.user.userId,
    type,
    type !== "downgrade"
  );

  const loadPreview = async () => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const res = await adminApi.userRefundPreview(detail.user.userId, {
        type,
        ...(type !== "downgrade" && { amount: Number(amount) }),
        ...(type === "downgrade" &&
          targetPlan !== "" && { targetPlan: targetPlan }),
      });
      if (res.code === 0 && res.result) {
        setPreview(res.result);
        return;
      }
      setPreviewError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setPreviewError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoadingPreview(false);
    }
  };

  // 확인 화면에 뜬 금액을 그대로 보낸다. 입력칸을 다시 읽으면 미리보기 왕복 중에 고친 값이
  // 나가서 "화면에서 본 숫자와 빠지는 숫자가 어긋날 수 없다" 는 약속이 깨진다.
  const run = () =>
    submit(() =>
      adminApi.userRefund(detail.user.userId, {
        type,
        ...(type !== "downgrade" &&
          preview && { amount: preview.distribution[0].cancelAmount }),
        ...(type === "downgrade" &&
          targetPlan !== "" && { targetPlan: targetPlan }),
        requestId,
        memo,
      })
    );

  if (preview) {
    return (
      <Modal
        title={`정말 ${REFUND_TITLE[kind]} 할까요?`}
        subject={subjectOf(detail)}
        onClose={() => setPreview(null)}
        footer={
          <>
            <Button onClick={() => setPreview(null)}>돌아가기</Button>
            <Button
              variant="dangerSolid"
              onClick={run}
              loading={submitting}
              loadingLabel="처리 중"
            >
              환불할게요
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <RefundPreviewRows preview={preview} />
          {memo.trim() && <InfoRow label="메모">{memo.trim()}</InfoRow>}

          <LeftoverOrders orderIds={preview.leftoverOrderIds} />
          <p className="text-sm leading-relaxed text-slate-600">
            되돌릴 수 없어요. 토스페이먼츠에서 처리가 끝나고 이용 상태와 플랜에
            반영되기까지 몇 초 걸릴 수 있어요.
          </p>
          {error && (
            <ErrorBanner>
              {`${error} 다시 누르기 전에 토스 콘솔에서 취소가 됐는지 확인해 주세요. 같은 팝업에서 다시 누르면 중복 취소는 막히지만, 팝업을 닫았다 열면 새 환불로 나가요.`}
            </ErrorBanner>
          )}
        </div>
      </Modal>
    );
  }

  const canProceed =
    type === "downgrade"
      ? targetPlan !== ""
      : Number(amount) > 0 && !refundAmountError(amount, base?.payAmount);

  return (
    <Modal
      title={REFUND_TITLE[kind]}
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="dangerSolid"
            onClick={loadPreview}
            loading={loadingPreview}
            loadingLabel="계산 중"
            disabled={!canProceed}
          >
            다음
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        {type === "downgrade" ? (
          <>
            <InfoRow label="기준 결제">
              <span className="font-mono text-xs">
                {base?.orderId ?? "없음"}
              </span>
            </InfoRow>
            <InfoRow label="현재 결제 금액">
              {formatAmount(base?.payAmount)}
            </InfoRow>
            <Field label="내릴 플랜">
              <Select
                value={targetPlan}
                onChange={(e) => setTargetPlan(e.target.value as AdminUserPlan)}
              >
                <option value="">플랜을 골라 주세요</option>
                {hints.map((hint) => (
                  <option key={hint.targetPlan} value={hint.targetPlan}>
                    {`${hint.targetPlanLabel} · 취소 ${formatAmount(hint.cancelAmount)}`}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        ) : (
          <>
            <RefundAmountSection
              base={base}
              amount={amount}
              setAmount={setAmount}
              presets={presets}
              helper="이용 상태와 플랜은 그대로 두고 결제 금액만 줄여요."
            />
            {/* 전액은 이 사유로도 되지만 대개는 프로그램 종료로 가야 할 건이다. 그때만 묻는다. */}
            {Number(amount) > 0 && Number(amount) === base?.payAmount && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                잔액 전액이에요. 전액 환불이 맞나요? 프로그램도 끝내야 하면
                프로그램 종료에서 처리해 주세요.
              </p>
            )}
          </>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {previewError && <ErrorBanner>{previewError}</ErrorBanner>}
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

// 기한 조정 방식. 일수는 방향까지 골라 두고, 날짜 지정은 고른 날짜가 방향을 정한다.
type ExpirationAdjustMode = "extendDays" | "shortenDays" | "until";

/**
 * 기한 조정: 이용 기한을 늘리거나 줄인다 (서버 AdminUserBO.extend).
 *
 * 바꾸는 값은 이용 기한 하나다. 이용 상태도 결제도 건드리지 않는다 (만료 회원 되살리기만 예외이고
 * 그건 연장에서만 일어난다). 무엇을 고르든 화면이 결과 날짜를 먼저 계산해 "며칠 연장 / 며칠 단축" 으로
 * 보여주고 버튼 글자가 그 방향을 따라간다.
 */
function ExpirationAdjustModal({ detail, onClose, onDone }: ModalProps) {
  const currentDate = detail.subscription.subscriptionExpirationDate;
  const [mode, setMode] = useState<ExpirationAdjustMode>("extendDays");
  const [days, setDays] = useState("14");
  const [untilDate, setUntilDate] = useState(currentDate || todayInputValue());
  // 이미 만료된 회원은 "오늘부터" 가 기본이다. 지난 기한에 며칠을 더해 봐야 여전히 과거라 보낼 수 없다
  const [fromToday, setFromToday] = useState(
    (daysBetweenInputValues(todayInputValue(), currentDate) ?? 0) < 0
  );
  const { memo, setMemo, submitting, error, submit, clearError } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const daysValid = /^\d+$/.test(days.trim()) && Number(days) > 0;
  // 서버에 보내는 일수는 부호가 방향이다 (음수면 단축)
  const signedDays = mode === "shortenDays" ? -Number(days) : Number(days);
  // "오늘부터" 는 늘리기에서만 쓴다. 켜 둔 채 줄이기로 바꿔도 미리보기와 보내는 값이
  // 갈라지지 않게 한 곳에서 정한다 (체크박스는 그때 화면에 없다).
  const fromTodayApplied = mode === "extendDays" && fromToday;
  const nextDate =
    mode === "until"
      ? untilDate
      : daysValid
        ? shiftDateInputValue(
            fromTodayApplied ? todayInputValue() : currentDate,
            signedDays
          )
        : null;
  const adjust = expirationAdjustOf(currentDate, nextDate);
  const blocked = !adjust.nextDate || !!adjust.error;

  const run = () =>
    submit(() =>
      adminApi.userExtend(detail.user.userId, {
        ...(mode === "until"
          ? { untilDate }
          : { days: signedDays, fromToday: fromTodayApplied }),
        memo,
      })
    );

  return (
    <Modal
      title="기한 조정"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          {/* 단축도 기한만 움직인다. 이용 상태도 결제 금액도 건드리지 않으므로 파괴적 액션 표기를 쓰지 않는다 */}
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={blocked}
          >
            {adjust.direction
              ? EXPIRATION_ADJUST_LABEL[adjust.direction]
              : "조정"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="현재 이용 기한">
          {formatDate(currentDate) || "없음"}
        </InfoRow>
        <InfoRow label="조정 뒤 기한">
          {/* 입력에 따라 값도 방향도 바뀌는 칸이라 읽어 주게 한다 */}
          <span
            className="flex flex-wrap items-center gap-2"
            aria-live="polite"
          >
            {adjust.nextDate ? formatDate(adjust.nextDate) : "-"}
            {adjust.direction && (
              <Badge tone={adjust.direction === "shorten" ? "warn" : "info"}>
                {`${adjust.days}일 ${EXPIRATION_ADJUST_LABEL[adjust.direction]}`}
              </Badge>
            )}
          </span>
        </InfoRow>

        <Field label="조정 방식">
          <Select
            value={mode}
            onChange={(e) => {
              clearError();
              setMode(e.target.value as ExpirationAdjustMode);
            }}
          >
            <option value="extendDays">일수만큼 늘리기</option>
            <option value="shortenDays">일수만큼 줄이기</option>
            <option value="until">날짜 지정</option>
          </Select>
        </Field>

        {mode === "until" ? (
          <Field
            label="이 날짜까지"
            helper="지정한 날짜로 이용 기한을 맞춰요. 지금보다 앞 날짜면 단축이에요."
            error={adjust.nextDate ? null : "날짜를 골라 주세요"}
          >
            <Input
              type="date"
              value={untilDate}
              onChange={(e) => {
                clearError();
                setUntilDate(e.target.value);
              }}
            />
          </Field>
        ) : (
          <>
            <Field
              label={mode === "shortenDays" ? "줄일 일수" : "늘릴 일수"}
              helper={
                mode === "shortenDays"
                  ? "현재 이용 기한에서 이만큼 빼요."
                  : "현재 이용 기한에서 이만큼 더해요."
              }
              error={
                days.trim() && !daysValid ? "1일 이상 숫자로 넣어 주세요" : null
              }
            >
              <Input
                inputMode="numeric"
                value={days}
                onChange={(e) => {
                  clearError();
                  setDays(e.target.value);
                }}
              />
            </Field>
            {mode === "extendDays" && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={fromToday}
                  onChange={(e) => setFromToday(e.target.checked)}
                />
                현재 이용 기한 대신 오늘부터 계산하기
              </label>
            )}
          </>
        )}

        {adjust.error && <ErrorBanner>{adjust.error}</ErrorBanner>}

        {detail.pause.isPaused && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            일시정지 중인 회원이에요. 이용 기한만 바뀌고 정지는 그대로 유지돼요.
          </p>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

/**
 * 일시정지: 시작일이 오늘이면 바로 정지하고, 미래 날짜면 그날 아침에 정지하도록 예약한다 (서버 AdminUserBO.pause).
 *
 * 팝업에는 언제 멈추고 언제 풀리는지, 기한이 며칠 늘어나는지 한 줄 요약만 둔다. 무엇이 바뀌는지 안내는
 * 기존 예약이 바뀌거나 회원 예약이 취소되는 경우처럼 버튼을 누르기 전에 알아야 하는 것만 남긴다.
 */
function PauseModal({ detail, onClose, onDone }: ModalProps) {
  const [startDate, setStartDate] = useState(todayInputValue());
  const [indefinite, setIndefinite] = useState(true);
  const [endDate, setEndDate] = useState(todayInputValue());
  const { memo, setMemo, submitting, error, submit, clearError } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const plan = pausePlanOf(
    startDate,
    indefinite ? null : endDate,
    detail.subscription.subscriptionExpirationDate
  );
  const scheduled = pauseScheduleLabel(detail.pause);
  const reservation = detail.pause.reservation;

  // 시작일을 마지막 날 뒤로 옮기면 마지막 날도 따라간다. 앞뒤가 뒤집힌 채로 남겨 두면 매번 두 칸을 고쳐야 한다
  const changeStartDate = (value: string) => {
    clearError();
    setStartDate(value);
    if ((daysBetweenInputValues(value, endDate) ?? 0) < 0) setEndDate(value);
  };

  const run = () =>
    submit(() =>
      adminApi.userPause(
        detail.user.userId,
        indefinite ? null : endDate,
        memo,
        plan.isFuture ? startDate : undefined
      )
    );

  return (
    <Modal
      title="일시정지"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            disabled={!!plan.error}
            loading={submitting}
            loadingLabel="처리 중"
          >
            {plan.isFuture ? "예약" : "정지"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="정지 시작일"
          helper="오늘이면 바로 정지되고, 미래 날짜면 그날 아침에 시작돼요."
        >
          <Input
            type="date"
            min={todayInputValue()}
            value={startDate}
            onChange={(e) => changeStartDate(e.target.value)}
          />
        </Field>

        <Field label="정지 방식">
          <Select
            value={indefinite ? "indefinite" : "until"}
            onChange={(e) => {
              clearError();
              setIndefinite(e.target.value === "indefinite");
            }}
          >
            <option value="indefinite">무기한 (재개는 직접)</option>
            <option value="until">종료일 지정</option>
          </Select>
        </Field>

        {!indefinite && (
          <Field
            label="정지 마지막 날"
            helper="이날까지 정지하고 다음 날 아침에 자동으로 재개돼요."
          >
            <Input
              type="date"
              min={startDate}
              value={endDate}
              onChange={(e) => {
                clearError();
                setEndDate(e.target.value);
              }}
            />
          </Field>
        )}

        {/* 입력에 따라 요약과 막힌 이유가 바뀌는 자리라 항상 두고 읽어 주게 한다 */}
        <div aria-live="polite">
          {plan.summary ? (
            <div className="flex flex-col gap-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
              <span className="text-xs font-medium text-slate-600">
                {plan.isFuture ? "예약" : "바로 정지"}
              </span>
              <span className="text-sm leading-relaxed text-slate-900">
                {plan.summary}
              </span>
            </div>
          ) : (
            plan.error && <ErrorBanner>{plan.error}</ErrorBanner>
          )}
        </div>

        {scheduled && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {`이미 잡아 둔 예약(${scheduled})이 있어요. 지금 잡는 ${
              plan.isFuture ? "예약으로" : "정지로"
            } 바뀌어요.`}
          </p>
        )}
        {reservation && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {`회원이 앱에서 잡아 둔 정지 예약(${monthDayLabel(
              reservation.startDate
            )} ~ ${monthDayLabel(reservation.endDate)})은 취소돼요.`}
          </p>
        )}
        {detail.pause.usedCount >= detail.pause.totalCount && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            회원이 쓸 수 있는 정지 횟수를 이미 다 썼어요. 어드민 정지는 그래도
            진행되지만, 회원의 남은 쿼터를 넘겨 쓰게 돼요.
          </p>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

/**
 * 정지 예약 취소: 예약만 지운다 (서버 AdminUserBO.cancelPauseSchedule).
 * 예약은 이용 상태·기한을 건드리지 않았으므로 되돌릴 것이 없다.
 */
function PauseCancelModal({ detail, onClose, onDone }: ModalProps) {
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserSubscriptionResult>(onDone);
  const { pause } = detail;

  const run = () =>
    submit(() => adminApi.userPauseCancel(detail.user.userId, memo));

  return (
    <Modal
      title="정지 예약 취소"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>닫기</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
          >
            예약 취소
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <InfoRow label="예약">{pauseScheduleLabel(pause) || "없음"}</InfoRow>
          <InfoRow label="예약한 사람">{pauseScheduledByLabel(pause)}</InfoRow>
        </div>
        <p className="text-sm leading-relaxed text-slate-700">
          예약만 지워요. 이용 상태와 이용 기한은 그대로예요.
        </p>
        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function ResumeModal({ detail, onClose, onDone }: ModalProps) {
  const [mode, setMode] = useState<"auto" | "until">("auto");
  const [extraDays, setExtraDays] = useState("0");
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserSubscriptionResult>(onDone);

  const extra = Number(extraDays) || 0;
  const restored = restoredExpirationDate(detail.pause, extra);
  const [untilDate, setUntilDate] = useState(restored || todayInputValue());

  const run = () =>
    submit(() =>
      adminApi.userResume(detail.user.userId, {
        ...(mode === "auto"
          ? extra > 0
            ? { extraDays: extra }
            : {}
          : { untilDate }),
        memo,
      })
    );

  return (
    <Modal
      title="일시정지 종료"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
          >
            재개
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="정지 경과">{`${detail.pause.pausedDays ?? 0}일`}</InfoRow>
        <InfoRow label="현재 이용 기한">
          {formatDate(detail.subscription.subscriptionExpirationDate) || "없음"}
        </InfoRow>

        <Field label="이용 기한 처리">
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as "auto" | "until")}
          >
            <option value="auto">정지한 일수만큼 자동 복원</option>
            <option value="until">이용 기한 직접 지정</option>
          </Select>
        </Field>

        {mode === "auto" ? (
          <>
            <Field
              label="추가 연장 일수"
              helper="복원한 기한에서 더 주고 싶은 일수만 적어요. 없으면 0."
            >
              <Input
                inputMode="numeric"
                value={extraDays}
                onChange={(e) => setExtraDays(e.target.value)}
              />
            </Field>
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {restored
                ? `처리 후 이용 기한은 ${formatDate(restored)} 이 돼요.`
                : "이용 기한이 없어 자동 복원을 계산할 수 없어요. 직접 지정해 주세요."}
            </p>
          </>
        ) : (
          <Field label="이용 기한">
            <Input
              type="date"
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
            />
          </Field>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ 페이백 검토

const PAYBACK_COMPLETION_HELP =
  "첫 100일을 모두 이용해야 합니다 (일시정지 기간은 빼고 셉니다). 신청은 100일을 채운 뒤 10일 안에 할 수 있어요. 신청 기한은 참고용으로 표기하며 충족 조건에는 넣지 않아요.";
const PAYBACK_FIRST_PAYMENT_HELP =
  "페이백 제도는 첫 100일 이용에만 적용되며, 프로그램 추가 연장 이용부터는 적용되지 않아요. 검토 기간은 전액 환불되지 않은 첫 결제부터 세요. 연장받은 경우도 100일까지만 봐요.";
const PAYBACK_PARTICIPATION_HELP =
  "결제 이후 첫 100일 동안 최소 주 1회 참여해야 해요. " +
  "활동지, 생각기록지, 명상훈련지 중 하나라도 완료한 날을 참여로 세요. " +
  "미완료 일수가 7일 이상 이어지면 빈 기간이에요. 일시정지 기간은 빼고 세요.";
const PAYBACK_MEDITATION_HELP =
  "명상 기록마다 마지막으로 재생한 위치를 더한 값이에요.";

const SELF_CHECKS = [
  { kind: "depression", label: "우울 (PHQ-9)" },
  { kind: "anxiety", label: "불안 (GAD-7)" },
  { kind: "stress", label: "스트레스 (PSS-10)" },
] as const;

const THOUGHT_RESULT_CLASS = {
  reduced: "text-emerald-700",
  same: "text-slate-600",
  increased: "text-amber-700",
  none: "text-slate-400",
} as const;

type PaybackDetailKey = "weekly" | "thoughts" | "meditations" | "selfChecks";

/**
 * 페이백 검토: 결제 후 첫 100일(정지 제외) 동안의 참여 사실 (서버 AdminPaybackReviewBO).
 *
 * 판단은 CS 가 한다. 페이백 버튼은 첫 결제(플랜 업그레이드 포함) 전액을 환불한다 (PaybackConfirmModal). 열 때 한 번 불러온다.
 * 조건(앱 FAQ 의 페이백 규정)마다 충족 여부를 보여주고, 부가 정보(자동적 사고·명상·검사)는 참고로 보여준다.
 * 주 1회 참여·자동적 사고·명상은 줄 아래로, 검사는 그래프를 상세로 펼쳐 본다. 자동적 사고는 개수와 점수 변화만 보여주고 내용은 싣지 않는다.
 */
function PaybackModal({
  detail,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  onClose: () => void;
  onDone: (result: AdminUserPaybackResult) => void;
}) {
  const [review, setReview] = useState<AdminUserPaybackReview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [open, setOpen] = useState<Record<PaybackDetailKey, boolean>>({
    weekly: false,
    thoughts: false,
    meditations: false,
    selfChecks: false,
  });
  const userId = detail.user.userId;

  useEffect(() => {
    // 닫힌 뒤 도착한 응답은 버린다
    let alive = true;
    adminApi
      .userPaybackReview(userId)
      .then((res) => {
        if (!alive) return;
        if (res.code === 0 && res.result) setReview(res.result);
        else setError(res.message || FALLBACK_ERROR_MESSAGE);
      })
      .catch(() => {
        if (alive) setError(FALLBACK_ERROR_MESSAGE);
      });
    return () => {
      alive = false;
    };
  }, [userId]);

  const toggle = (key: PaybackDetailKey) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  // 이용 중이거나 정지 중: 기간이 아직 이어지고, 페이백하면 프로그램도 끝난다 (웹훅이 같은 기준으로 가린다)
  const isOngoing = canTerminateState(detail);

  if (review && confirming) {
    return (
      <PaybackConfirmModal
        detail={detail}
        review={review}
        isOngoing={isOngoing}
        onBack={() => setConfirming(false)}
        onDone={onDone}
      />
    );
  }

  const blockedReason = review?.refund.blockedReason;
  const breakdown = review && paybackRefundBreakdown(review.refund);
  return (
    <Modal
      title="페이백"
      subject={subjectOf(detail)}
      width="720px"
      onClose={onClose}
      footer={
        review && (
          <>
            {blockedReason ? (
              <span className="mr-auto text-sm text-slate-500">
                {blockedReason}
              </span>
            ) : (
              <span className="mr-auto flex flex-wrap items-baseline gap-x-2 text-sm">
                <span className="text-slate-600">
                  페이백 금액{" "}
                  <span className="font-semibold text-slate-900">
                    {formatAmount(paybackRefundTotal(review.refund))}
                  </span>
                </span>
                {breakdown && (
                  <span className="text-xs text-slate-500">{breakdown}</span>
                )}
              </span>
            )}
            <Button
              variant="dangerSolid"
              disabled={!!blockedReason}
              onClick={() => setConfirming(true)}
            >
              페이백하기
            </Button>
          </>
        )
      }
    >
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {!review && !error && (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="block h-5 w-full animate-pulse rounded bg-slate-100"
            />
          ))}
        </div>
      )}
      {review && (
        <PaybackReviewBody
          review={review}
          isOngoing={isOngoing}
          open={open}
          toggle={toggle}
        />
      )}
    </Modal>
  );
}

/**
 * 페이백 확인: 첫 결제(플랜 업그레이드 포함) 전액을 토스에서 취소한다 (사유 "페이백").
 *
 * 금액은 서버가 계산해 준 그대로다. 고칠 칸이 없다. 서버는 보낸 결제 목록을 다시 고른 대상과 견주고, 다르면 거부한다.
 * 금액과 충족하지 않은 조건을 여기서 한 번 더 보여 준다. 이용 중이던 회원의 프로그램 종료는 토스 웹훅이 몇 초 뒤에 한다.
 * 최근 결제부터 취소하다 일부만 나가면 실패한 건을 보여 준다.
 */
function PaybackConfirmModal({
  detail,
  review,
  isOngoing,
  onBack,
  onDone,
}: {
  detail: AdminUserDetail;
  review: AdminUserPaybackReview;
  isOngoing: boolean;
  onBack: () => void;
  onDone: (result: AdminUserPaybackResult) => void;
}) {
  const { refund, extensionPaidAt } = review;
  // 확인 창 1회 열림 = 페이백 1건. 재시도해도 같은 값을 보내 토스가 중복 취소를 걸러낸다.
  const [requestId] = useState(() => crypto.randomUUID());
  const [partial, setPartial] = useState<AdminUserPaybackResult | null>(null);
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserPaybackResult>((result) =>
      result.failed.length > 0 ? setPartial(result) : onDone(result)
    );
  const total = paybackRefundTotal(refund);
  const unmet = paybackUnmetConditions(review, isOngoing);
  const usage = paybackUsageChange(detail);

  if (partial) {
    return (
      <Modal
        title="페이백을 일부만 처리했어요"
        subject={subjectOf(detail)}
        onClose={() => onDone(partial)}
        footer={<Button onClick={() => onDone(partial)}>확인</Button>}
      >
        <div className="flex flex-col gap-3">
          {partial.canceled.map((item) => (
            <InfoRow key={item.orderId} label={`${item.label} 환불`}>
              {paybackRefundItemLabel(item)}
            </InfoRow>
          ))}
          <ErrorBanner>
            {`${partial.failed
              .map((item) => `${item.label}(${paybackRefundItemLabel(item)})`)
              .join(
                ", "
              )} 취소에 실패했어요. 토스 콘솔에서 사유 "페이백" 으로 마저 취소해 주세요.`}
          </ErrorBanner>
        </div>
      </Modal>
    );
  }

  const run = () =>
    submit(() =>
      adminApi.userPayback(detail.user.userId, {
        items: refund.items.map(({ orderId, amount }) => ({ orderId, amount })),
        requestId,
        memo,
      })
    );

  return (
    <Modal
      title="정말 페이백할까요?"
      subject={subjectOf(detail)}
      onClose={onBack}
      footer={
        <>
          <Button onClick={onBack}>돌아가기</Button>
          <Button
            variant="dangerSolid"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
          >
            페이백하기
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <InfoRow label="환불 금액">
          <span className="font-semibold">{formatAmount(total)}</span>
        </InfoRow>
        {refund.items.map((item) => (
          <InfoRow key={item.orderId} label={item.label}>
            {paybackRefundItemLabel(item)}
          </InfoRow>
        ))}
        {extensionPaidAt.length > 0 && (
          <InfoRow label="연장 결제">
            {paybackExtensionLabel(extensionPaidAt)}
          </InfoRow>
        )}
        {unmet.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-sm font-medium text-amber-800">
              충족하지 않은 조건이 있어요
            </p>
            <ul className="mt-1 list-disc pl-5 text-sm text-amber-700">
              {unmet.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
        <InfoRow label="이용 상태">{usage.label}</InfoRow>
        {usage.endsToday && (
          <InfoRow label="현재 이용 기한">
            {formatDate(detail.subscription.subscriptionExpirationDate) ||
              "없음"}
          </InfoRow>
        )}
        <InfoRow label="취소 사유">
          <span className="font-mono text-xs">페이백</span>
        </InfoRow>
        <MemoField
          memo={memo}
          setMemo={setMemo}
          placeholder="예: 페이백 조건 확인, 회원 요청"
        />
        <p className="text-sm leading-relaxed text-slate-600">
          {`되돌릴 수 없어요. 토스페이먼츠에서 처리가 끝나고 ${
            usage.endsToday ? "이용 상태" : "결제 이력"
          }에 반영되기까지 몇 초 걸릴 수 있어요.`}
        </p>
        {error && (
          <ErrorBanner>
            {`${error} 다시 누르기 전에 토스 콘솔에서 취소가 됐는지 확인해 주세요. 같은 창에서 다시 누르면 중복 취소는 막혀요.`}
          </ErrorBanner>
        )}
      </div>
    </Modal>
  );
}

function PaybackReviewBody({
  review,
  isOngoing,
  open,
  toggle,
}: {
  review: AdminUserPaybackReview;
  isOngoing: boolean;
  open: Record<PaybackDetailKey, boolean>;
  toggle: (key: PaybackDetailKey) => void;
}) {
  const { gaps, weeks, automaticThoughts, meditations } = review;

  return (
    <div className="flex flex-col gap-5">
      {review.resetWithoutSnapshotAt && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {`${monthDayLabel(
            review.resetWithoutSnapshotAt
          )} 초기화 이전의 활동지·명상·자동적 사고 기록은 빠져 있어요.`}
        </p>
      )}

      <PaybackSection title="조건">
        <PaybackRow label="100일 이용" help={PAYBACK_COMPLETION_HELP}>
          <PaybackConditionValue
            condition={paybackCompletionCondition(review, isOngoing)}
          />
        </PaybackRow>
        <PaybackRow label="최초 결제" help={PAYBACK_FIRST_PAYMENT_HELP}>
          <PaybackConditionValue
            condition={paybackFirstPaymentCondition(review)}
          />
        </PaybackRow>
        <PaybackRow
          label="주 1회 참여"
          help={PAYBACK_PARTICIPATION_HELP}
          open={open.weekly}
          onToggle={weeks.length > 0 ? () => toggle("weekly") : undefined}
          last
        >
          <span className="flex flex-col gap-1">
            <PaybackConditionValue condition={paybackWeeklyCondition(review)} />
            <span className="text-slate-500">
              {paybackPausesLabel(review.pauses)}
            </span>
          </span>
        </PaybackRow>
        {open.weekly && (
          <div className="mb-1 mt-1 flex flex-col gap-3">
            {gaps.length > 0 && (
              <div className="flex flex-col gap-1 text-sm">
                <span className="text-xs font-semibold text-amber-700">
                  7일 이상 빈 기간
                </span>
                {gaps.map((gap) => (
                  <span key={gap.from} className="text-slate-900">
                    {paybackGapLabel(gap)}
                  </span>
                ))}
              </div>
            )}
            <Table>
              <THead>
                <TH>주차</TH>
                <TH>기간</TH>
                <TH>참여한 날</TH>
                <TH>활동</TH>
              </THead>
              <tbody>
                {weeks.map((week) => (
                  <TRow key={week.week}>
                    <TD muted>{`W${week.week}`}</TD>
                    <TD>{`${monthDayLabel(week.from)} ~ ${monthDayLabel(
                      week.to
                    )}`}</TD>
                    <TD>{paybackWeekDaysLabel(week)}</TD>
                    <TD>
                      {/* 색은 안쪽에 준다. TD 의 기본 글자색이 className 보다 뒤에 생성돼 이긴다 */}
                      <span
                        className={
                          week.activeDays === 0 ? "text-amber-700" : undefined
                        }
                      >
                        {paybackWeekActivityLabel(week)}
                      </span>
                    </TD>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </PaybackSection>

      <PaybackSection title="부가 정보">
        <PaybackRow
          label="자동적 사고"
          open={open.thoughts}
          onToggle={
            automaticThoughts.length > 0 ? () => toggle("thoughts") : undefined
          }
        >
          {paybackThoughtSummary(automaticThoughts)}
        </PaybackRow>
        {open.thoughts && (
          <div className="mb-3 mt-1">
            <Table>
              <THead>
                <TH>#</TH>
                <TH>찾은 날</TH>
                <TH>영향 점수</TH>
                <TH>결과</TH>
              </THead>
              <tbody>
                {automaticThoughts.map((thought, index) => {
                  const result = paybackThoughtResult(thought);
                  return (
                    <TRow key={`${thought.foundAt}-${index}`}>
                      <TD muted>{index + 1}</TD>
                      <TD>{monthDayLabel(thought.foundAt)}</TD>
                      <TD>{paybackThoughtScoreLabel(thought)}</TD>
                      <TD>
                        <span className={THOUGHT_RESULT_CLASS[result.tone]}>
                          {result.label}
                        </span>
                      </TD>
                    </TRow>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <PaybackRow
          label="명상"
          help={PAYBACK_MEDITATION_HELP}
          open={open.meditations}
          onToggle={
            meditations && meditations.length > 0
              ? () => toggle("meditations")
              : undefined
          }
        >
          {meditations ? (
            paybackMeditationSummary(meditations)
          ) : (
            <span className="text-slate-500">불러오지 못했어요</span>
          )}
        </PaybackRow>
        {open.meditations && meditations && (
          <div className="mb-3 mt-1">
            <Table>
              <THead>
                <TH>날짜</TH>
                <TH>명상</TH>
                <TH>재생 시간</TH>
              </THead>
              <tbody>
                {meditations.map((meditation, index) => (
                  <TRow key={`${meditation.doneAt}-${index}`}>
                    <TD>{monthDayLabel(meditation.doneAt)}</TD>
                    <TD>{meditation.name || "-"}</TD>
                    <TD>{meditationMinutesLabel(meditation.seconds)}</TD>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <PaybackRow
          label="검사"
          alignTop
          last
          open={open.selfChecks}
          onToggle={
            SELF_CHECKS.some(({ kind }) => (review[kind] ?? []).length > 0)
              ? () => toggle("selfChecks")
              : undefined
          }
        >
          <div className="flex flex-col gap-3">
            {SELF_CHECKS.map(({ kind, label }) => {
              const points = review[kind] ?? [];
              return (
                <div key={kind} className="flex flex-col gap-1.5">
                  <span className="flex flex-wrap gap-x-2">
                    <span className="text-slate-500">{label}</span>
                    <span>{selfCheckSummaryLabel(kind, points)}</span>
                  </span>
                  {/* 그래프는 상세를 펼쳤을 때만 */}
                  {open.selfChecks && points.length > 0 && (
                    <SelfCheckChart kind={kind} label={label} points={points} />
                  )}
                </div>
              );
            })}
          </div>
        </PaybackRow>
      </PaybackSection>
    </div>
  );
}

function PaybackSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col">
      <h3 className="border-b border-slate-200 pb-1.5 text-xs font-semibold text-slate-500">
        {title}
      </h3>
      {children}
    </section>
  );
}

// 조건 한 줄의 값: 충족 여부 배지와 근거
function PaybackConditionValue({ condition }: { condition: PaybackCondition }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone={condition.met ? "ok" : "warn"}>{condition.badge}</Badge>
      <span className="text-slate-500">{condition.detail}</span>
    </span>
  );
}

// 검사 점수 약식 그래프: 검사 순서대로 점을 잇고 점마다 점수를 적는다 (날짜 간격은 무시한다).
// 점 색은 검사 결과 구간마다 다르고(정상·가벼움·중간·심함·극심함), 세로 눈금은 구간이 시작하는 점수다.
// 마우스를 올리면 가장 가까운 점의 날짜·점수·심각도가 툴팁으로 바로 뜬다.
// 브라우저 title 툴팁은 1초쯤 가만히 올려 둬야 떠서 직접 그린다 (HelpTip 과 같은 이유).
// 스트레스는 눈금(13·16·19)이 3점 간격이라, 눈금 숫자가 겹치지 않게 세로를 넉넉히 둔다.
const CHART = {
  width: 480,
  height: 168,
  left: 28,
  right: 464,
  top: 20,
  bottom: 144,
};
// 구간 순서대로: 정상 초록 · 가벼움 노랑 · 중간 주황 · 심함 빨강 · 극심함 진한 빨강 (불안·스트레스는 4구간이라 심함까지)
const SEVERITY_FILL = [
  "fill-emerald-600",
  "fill-yellow-500",
  "fill-orange-500",
  "fill-red-600",
  "fill-red-900",
];

function SelfCheckChart({
  kind,
  label,
  points,
}: {
  kind: SelfCheckKind;
  label: string;
  points: AdminUserSelfCheckPoint[];
}) {
  const [active, setActive] = useState<number | null>(null);
  const plotted = selfCheckChartPoints(kind, points, CHART);
  const max = selfCheckMaxScore(kind);
  const hovered = active === null ? undefined : plotted[active];
  const tooltip = hovered && selfCheckTooltip(kind, hovered.point);
  return (
    <div
      className="relative w-full max-w-[480px]"
      onMouseLeave={() => setActive(null)}
    >
      {/* overflow-visible: 주차가 없어 날짜로 적는 끝 점 라벨("8월 29일")이 그래프 오른쪽 끝에서 잘리지 않게 */}
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        className="block h-auto w-full overflow-visible"
        role="img"
        aria-label={`${label} 점수 그래프`}
      >
        <line
          x1={CHART.left - 8}
          x2={CHART.width}
          y1={CHART.bottom}
          y2={CHART.bottom}
          className="stroke-slate-200"
        />
        {selfCheckTicks(kind).map((tick) => {
          const y = CHART.bottom - ((CHART.bottom - CHART.top) * tick) / max;
          return (
            <g key={tick}>
              {tick > 0 && (
                <line
                  x1={CHART.left - 8}
                  x2={CHART.width}
                  y1={y}
                  y2={y}
                  strokeDasharray="3 3"
                  className="stroke-slate-200"
                />
              )}
              <text x={0} y={y + 3} fontSize={9} className="fill-slate-400">
                {tick}
              </text>
            </g>
          );
        })}
        {hovered && (
          <line
            x1={hovered.x}
            x2={hovered.x}
            y1={CHART.top - 12}
            y2={CHART.bottom}
            className="stroke-slate-300"
          />
        )}
        {plotted.length > 1 && (
          <polyline
            points={plotted.map(({ x, y }) => `${x},${y}`).join(" ")}
            fill="none"
            strokeWidth={1.5}
            className="stroke-slate-300"
          />
        )}
        {plotted.map(({ x, y, point }, index) => (
          <g key={`${point.date}-${index}`}>
            <circle
              cx={x}
              cy={y}
              r={index === active ? 6 : 4}
              strokeWidth={index === active ? 2 : 0}
              className={`stroke-white ${
                SEVERITY_FILL[selfCheckSeverityLevel(kind, point.score)]
              }`}
            />
            <text
              x={x}
              y={y - 8}
              textAnchor="middle"
              fontSize={10}
              fontWeight={600}
              className="fill-slate-700"
            >
              {point.score}
            </text>
            <text
              x={x}
              y={CHART.height - 6}
              textAnchor="middle"
              fontSize={9}
              className="fill-slate-400"
            >
              {point.week ? `W${point.week}` : monthDayLabel(point.date)}
            </text>
          </g>
        ))}
        {/* 맨 위에 투명하게 깔아 마우스를 받는다. 점마다 이웃 점과의 가운데까지 */}
        {selfCheckHoverRanges(
          plotted.map(({ x }) => x),
          CHART.width
        ).map(({ from, to }, index) => (
          <rect
            key={`${from}-${to}`}
            x={from}
            y={0}
            width={to - from}
            height={CHART.height}
            fill="transparent"
            onMouseEnter={() => setActive(index)}
          />
        ))}
      </svg>
      {hovered && tooltip && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-30 whitespace-nowrap rounded-md bg-slate-900 px-3 py-2 text-xs text-white shadow-lg"
          style={{
            left: `${(hovered.x / CHART.width) * 100}%`,
            top: `${(hovered.y / CHART.height) * 100}%`,
            // 점 위 점수 숫자까지 비켜서 위에 띄운다. 위쪽에 붙은 점은 그래프 위로 나가지 않게 아래로,
            // 양 끝 점은 그래프 안쪽으로 붙인다. 모달 본문 스크롤 영역에 잘리지 않게 그래프 안에 둔다
            transform: `translate(${
              hovered.x < CHART.width / 4
                ? "-16px"
                : hovered.x > (CHART.width * 3) / 4
                  ? "calc(-100% + 16px)"
                  : "-50%"
            }, ${hovered.y < 76 ? "14px" : "calc(-100% - 22px)"})`,
          }}
        >
          <div className="text-slate-300">{tooltip.date}</div>
          <div className="mt-0.5 flex items-center gap-1.5 font-semibold">
            <svg viewBox="0 0 8 8" className="h-2 w-2 shrink-0" aria-hidden>
              <circle
                cx={4}
                cy={4}
                r={4}
                className={
                  SEVERITY_FILL[
                    selfCheckSeverityLevel(kind, hovered.point.score)
                  ]
                }
              />
            </svg>
            {tooltip.score}
          </div>
        </div>
      )}
    </div>
  );
}

// 검토 한 줄: 라벨·값, 상세가 있으면 오른쪽에 펼침 버튼
function PaybackRow({
  label,
  help,
  open,
  onToggle,
  alignTop = false,
  last = false,
  children,
}: {
  label: string;
  help?: string;
  open?: boolean;
  onToggle?: () => void;
  alignTop?: boolean;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={`flex gap-3 py-2.5 text-sm ${
        alignTop ? "items-start" : "items-center"
      } ${last ? "" : "border-b border-slate-100"}`}
    >
      <span className="w-32 shrink-0 text-slate-500">
        {label}
        {help && <HelpTip text={help} />}
      </span>
      <span className="min-w-0 flex-1 text-slate-900">{children}</span>
      {onToggle && (
        <Button
          size="sm"
          onClick={onToggle}
          aria-expanded={!!open}
          aria-label={`${label} ${open ? "접기" : "상세"}`}
        >
          {open ? "접기" : "상세"}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d={open ? "m6 15 6-6 6 6" : "m6 9 6 6 6-6"} />
          </svg>
        </Button>
      )}
    </div>
  );
}

/**
 * 프로그램 콘텐츠를 처음 상태로 되돌린다.
 *
 * 프로그램 종료와 같은 2단계다. 되돌릴 수 없고, 지워지는 범위가 넓어서 무엇이 남고 무엇이 사라지는지
 * 확인 화면에서 한 번 더 보여준다.
 */
function ResetModal({
  detail,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const { memo, setMemo, submitting, error, submit } =
    useAction<AdminUserResetResult>(onDone);

  const run = () => submit(() => adminApi.userReset(detail.user.userId, memo));

  if (confirming) {
    return (
      <Modal
        title="정말 초기화할까요?"
        subject={subjectOf(detail)}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>돌아가기</Button>
            <Button
              variant="danger"
              onClick={run}
              loading={submitting}
              loadingLabel="처리 중"
            >
              초기화할게요
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm leading-relaxed text-slate-700">
            회원이 지금까지 작성한 내용이 전부 사라져요. 되돌릴 수 없고, 회원이
            직접 쓴 생각기록지와 활동지 답변도 복구할 방법이 없어요.
          </p>
          {memo.trim() && <InfoRow label="메모">{memo.trim()}</InfoRow>}
          {error && <ErrorBanner>{error}</ErrorBanner>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="프로그램 초기화"
      subject={subjectOf(detail)}
      description="프로그램을 처음 상태로 되돌리고 초기 활동지를 다시 열어요."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="danger" onClick={() => setConfirming(true)}>
            초기화
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-sm font-medium text-amber-800">지워지는 것</p>
          <p className="mt-1 text-sm leading-relaxed text-amber-700">
            활동지와 답변, 생각기록지, 명상훈련지, 알림 예약, 개념화 데이터까지
            프로그램 진행 내용이 전부 지워져요.
          </p>
          <p className="mt-2 text-sm font-medium text-amber-800">남는 것</p>
          <p className="mt-1 text-sm leading-relaxed text-amber-700">
            이용 기한과 담당 코치, 채팅방은 그대로예요. 채팅 내용도 남아요.
          </p>
        </div>

        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

/**
 * 프로그램 종료. 환불을 체크하면 같은 처리가 토스 취소까지 함께 나간다.
 *
 * 종료와 환불을 한 팝업에 둔 이유: CS 는 "환불하고 끝내 주세요" 를 한 건으로 받는다. 버튼을
 * 나눠 두면 둘 중 하나만 눌리고 나머지를 잊는 사고가 난다 (실제로 이 화면의 첫 버전은 종료만
 * 하고 환불은 토스 콘솔에서 따로 하라고 안내했다).
 *
 * 두 경로는 서버도 결과도 다르다:
 *  - 환불 없음 → /admin/user/terminate. 상태가 즉시 바뀌고 종료 푸시도 그 자리에서 나간다.
 *  - 환불 포함 → /admin/user/refund(terminate). 토스 취소만 나가고 상태·종료 푸시는 웹훅이 처리한다.
 *
 * 어느 쪽이든 회원이 받는 안내는 같다. 환불 여부로 알림이 갈리지 않게 양쪽 다 보낸다.
 *
 * 이미 만료·종료된 회원은 상태를 더 바꿀 게 없다. 그래도 환불 요청은 들어오므로 팝업은 열리고,
 * 대신 환불 전용으로 고정된다(체크를 끌 수 없다).
 */
function TerminateModal({
  detail,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  onClose: () => void;
  // 환불 경로는 이 자리에서 상태가 확정되지 않는다(웹훅이 늦게 바꾼다). 화면이 먼저 그릴 낙관적 결과와
  // 실제로 끝난 토스 취소 내역을 함께 넘긴다.
  onDone: (
    result: AdminUserSubscriptionResult,
    refund?: AdminUserRefundResult
  ) => void;
}) {
  // 되돌릴 수 없는 처리라 입력 화면과 최종 확인을 분리한다.
  // 브라우저 confirm 을 쓰지 않는다. 어드민 문구와 톤이 어긋나고 회원 정보를 같이 보여줄 수 없다.
  // 상태를 바꿀 수 있는 회원인지. 아니면 이 팝업은 환불만 한다.
  const canChangeState = canTerminateState(detail);
  const [confirming, setConfirming] = useState(false);
  const [withRefund, setWithRefund] = useState(!canChangeState);
  const [amount, setAmount] = useState("");
  const [cancelReasonNote, setCancelReasonNote] = useState("");
  // 팝업 1회 열림 = 환불 1건. 재시도해도 같은 값을 보내 토스가 중복 취소를 걸러낸다.
  const [requestId] = useState(() => crypto.randomUUID());
  const [preview, setPreview] = useState<AdminUserRefundPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { memo, setMemo, submitting, error, submit, clearError } = useAction<
    AdminUserSubscriptionResult | AdminUserRefundResult
  >((result) =>
    // 환불 응답에는 구독 상태가 없다(웹훅이 나중에 바꾼다). 웹훅이 만들 상태를 화면이 먼저 그린다.
    withRefund
      ? onDone(
          optimisticTerminateResult(detail),
          result as AdminUserRefundResult
        )
      : onDone(result as AdminUserSubscriptionResult)
  );

  const base = refundBasePayment(detail);
  // 환불 전제조건은 서버 refundPlanOf 와 같다. 종료 자체는 막지 않고 체크박스만 잠근다.
  const refundBlockReason = actionBlockReason(detail, "refundTerminate");
  const presets = useRefundPresets(
    detail.user.userId,
    "terminate",
    withRefund && !refundBlockReason
  );

  // 환불 금액은 확인 화면에 뜬 미리보기 값을 그대로 보낸다. 입력칸을 다시 읽으면
  // 미리보기 왕복 중에 고친 값이 나간다.
  const run = () =>
    withRefund && preview
      ? submit(() =>
          adminApi.userRefund(detail.user.userId, {
            type: "terminate",
            amount: preview.distribution[0].cancelAmount,
            ...(cancelReasonNote.trim() && {
              cancelReasonNote: cancelReasonNote.trim(),
            }),
            requestId,
            memo,
          })
        )
      : submit(() => adminApi.userTerminate(detail.user.userId, memo));

  // 환불이 붙으면 실제로 나갈 금액과 취소 사유를 서버에 한 번 물어보고 확인 화면으로 넘어간다.
  const proceed = async () => {
    // 앞선 시도의 오류가 다음 확인 화면에 그대로 남으면, 하지도 않은 취소를 확인하라고 안내하게 된다.
    clearError();
    setPreviewError(null);
    if (!withRefund) {
      setPreview(null);
      setConfirming(true);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await adminApi.userRefundPreview(detail.user.userId, {
        type: "terminate",
        amount: Number(amount),
        ...(cancelReasonNote.trim() && {
          cancelReasonNote: cancelReasonNote.trim(),
        }),
      });
      if (res.code === 0 && res.result) {
        setPreview(res.result);
        setConfirming(true);
        return;
      }
      setPreviewError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setPreviewError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoadingPreview(false);
    }
  };

  if (confirming) {
    return (
      <Modal
        title={withRefund ? "정말 환불하고 종료할까요?" : "정말 종료할까요?"}
        subject={subjectOf(detail)}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>돌아가기</Button>
            <Button
              variant={withRefund ? "dangerSolid" : "danger"}
              onClick={run}
              loading={submitting}
              loadingLabel="처리 중"
            >
              {withRefund ? "환불하고 종료할게요" : "종료할게요"}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {withRefund && preview && <RefundPreviewRows preview={preview} />}
          <InfoRow label="현재 이용 기한">
            {formatDate(detail.subscription.subscriptionExpirationDate) ||
              "없음"}
          </InfoRow>
          {/* 이미 만료·종료된 회원은 기한이 그대로다. 웹훅도 만료된 구독은 건드리지 않는다. */}
          {canChangeState && <InfoRow label="종료 후">오늘까지</InfoRow>}
          {memo.trim() && <InfoRow label="메모">{memo.trim()}</InfoRow>}

          {/* 종료 푸시는 환불 여부와 무관하게 나간다. 끌 수 없으니 두 경로 모두에서 알린다. */}
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            {detail.user.progressStatus === "terminated"
              ? "이미 종료된 회원이지만 프로그램 종료 푸시는 또 나가요. 종료를 이미 안내한 회원이면 채팅으로 먼저 설명해 주세요."
              : "회원에게 프로그램 종료 푸시가 발송돼요. 채팅으로 먼저 합의된 건인지 확인해 주세요."}
          </p>

          {withRefund ? (
            <>
              {preview && (
                <LeftoverOrders orderIds={preview.leftoverOrderIds} />
              )}
              <p className="text-sm leading-relaxed text-slate-600">
                되돌릴 수 없어요. 토스페이먼츠에서 처리가 끝나고 이용 상태에
                반영되기까지 몇 초 걸릴 수 있어요.
              </p>
            </>
          ) : (
            <p className="text-sm leading-relaxed text-slate-700">
              지금 바로 프로그램이 종료되고 회원은 더 이상 이용할 수 없어요.
              되돌리려면 기한 조정으로 다시 살려야 하는데, 이미 지난 기간은
              돌아오지 않아요.
            </p>
          )}
          {error && (
            <ErrorBanner>
              {withRefund
                ? `${error} 다시 누르기 전에 토스 콘솔에서 취소가 됐는지 확인해 주세요. 같은 팝업에서 다시 누르면 중복 취소는 막히지만, 팝업을 닫았다 열면 새 환불로 나가요.`
                : error}
            </ErrorBanner>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="프로그램 종료"
      subject={subjectOf(detail)}
      description={
        canChangeState
          ? "프로그램을 즉시 종료합니다. 이용 기한이 오늘 날짜로 설정됩니다."
          : "이미 종료된 회원이라 이용 상태는 그대로입니다. 환불만 처리합니다."
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant={withRefund ? "dangerSolid" : "danger"}
            onClick={proceed}
            loading={loadingPreview}
            loadingLabel="계산 중"
            disabled={
              withRefund &&
              !(
                Number(amount) > 0 &&
                !refundAmountError(amount, base?.payAmount)
              )
            }
          >
            종료
          </Button>
        </>
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        <Toggle
          checked={withRefund}
          onChange={(next) => {
            clearError();
            setWithRefund(next);
          }}
          // 상태를 바꿀 게 없는 회원은 환불이 이 팝업의 유일한 처리다. 끄면 아무 일도 안 하게 된다.
          disabled={!!refundBlockReason || !canChangeState}
          label="환불도 같이 처리"
          helper={
            refundBlockReason ??
            (!canChangeState
              ? "이미 종료된 회원이라 환불만 처리해요."
              : withRefund
                ? "결제를 취소하고 프로그램도 종료해요."
                : "이용 상태만 바꾸고 결제는 그대로 둬요.")
          }
        />

        {withRefund && (
          <>
            <RefundAmountSection
              base={base}
              amount={amount}
              setAmount={setAmount}
              presets={presets}
            />
            <Field
              label="토스 취소 사유 메모"
              helper="비워도 괜찮아요. 적으면 토스 취소 사유에 함께 남아요."
            >
              <Input
                value={cancelReasonNote}
                maxLength={100}
                onChange={(e) => setCancelReasonNote(e.target.value)}
                placeholder="예: CS 협의 건"
              />
            </Field>
          </>
        )}

        <MemoField memo={memo} setMemo={setMemo} />
        {previewError && <ErrorBanner>{previewError}</ErrorBanner>}
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function NicknameModal({
  detail,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [nickname, setNickname] = useState(detail.user.rawNickname || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memo, setMemo] = useState("");

  const run = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminApi.userNickname(
        detail.user.userId,
        nickname.trim(),
        memo
      );
      if (res.code === 0 && res.result) {
        if (!res.result.chatSynced && detail.coach?.hasChatChannel) {
          window.alert(
            "닉네임은 바뀌었지만 채팅방 이름 동기화에 실패했어요. 채팅방 표시를 확인해 주세요."
          );
        }
        onDone();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  const trimmed = nickname.trim();
  const invalid = trimmed.length === 0 || trimmed.length > 12;

  return (
    <Modal
      title="닉네임 변경"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={invalid || trimmed === detail.user.rawNickname}
          >
            변경
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="현재 닉네임">{detail.user.nickname || "없음"}</InfoRow>
        <Field
          label="새 닉네임"
          helper="12자까지 쓸 수 있어요. 같은 닉네임이 있으면 순번이 자동으로 붙어요."
          error={trimmed.length > 12 ? "12자 이하로 입력해 주세요" : null}
        >
          <Input
            value={nickname}
            invalid={trimmed.length > 12}
            onChange={(e) => setNickname(e.target.value)}
          />
        </Field>
        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}

function CoachModal({
  detail,
  coaches,
  onClose,
  onDone,
}: {
  detail: AdminUserDetail;
  coaches: AdminUserCoachRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [coachId, setCoachId] = useState("");
  const [sendAnnouncement, setSendAnnouncement] = useState(true);
  const [memo, setMemo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminApi.userCoachChange(
        detail.user.userId,
        Number(coachId),
        sendAnnouncement,
        memo
      );
      if (res.code === 0 && res.result) {
        if (res.result.warning) window.alert(res.result.warning);
        if (sendAnnouncement && !res.result.announcementSent) {
          window.alert(
            "코치는 변경했지만 안내 메시지는 나가지 않았어요. 오늘 이미 안내가 나갔거나 채팅방이 없는 회원이에요."
          );
        }
        onDone();
        return;
      }
      setError(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setError(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  // 비재직 관리자 코치는 프로그램 시작 전용이다. 코치 변경은 재직 코치만 고른다
  const selectable = coaches.filter(
    (coach) => coach.isActive && coach.coachId !== detail.coach?.coachId
  );

  return (
    <Modal
      title="담당 코치 변경"
      subject={subjectOf(detail)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button
            variant="primary"
            onClick={run}
            loading={submitting}
            loadingLabel="처리 중"
            disabled={!coachId}
          >
            변경
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <InfoRow label="현재 코치">
          {detail.coach ? detail.coach.coachName : "없음"}
        </InfoRow>
        <Field label="새 담당 코치">
          <Select value={coachId} onChange={(e) => setCoachId(e.target.value)}>
            <option value="">코치를 골라 주세요</option>
            {selectable.map((coach) => (
              <option key={coach.coachId} value={coach.coachId}>
                {`${coach.name} (${coach.assignedPatientCount}/${coach.assignCapCount}명)`}
                {coach.assignedPatientCount >= coach.assignCapCount
                  ? " · 상한 도달"
                  : ""}
                {coach.isMasterPlanExcluded ? " · 마스터 제외" : ""}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={sendAnnouncement}
            onChange={(e) => setSendAnnouncement(e.target.checked)}
          />
          채팅방에 코치 변경 안내를 보내기
        </label>
        {!detail.coach?.hasChatChannel && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
            채팅방이 없는 회원이라 채팅 멤버 교체와 안내 발송이 일어나지 않아요.
          </p>
        )}
        <MemoField memo={memo} setMemo={setMemo} />
        {error && <ErrorBanner>{error}</ErrorBanner>}
      </div>
    </Modal>
  );
}
